import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

/**
 * AI Deal Assistant panel — rendered inside the Deal Room.
 * Party-scoped: each user only ever sees THEIR AI wallet + THEIR conversation.
 * $10 initial deposit required before chat unlocks.
 *
 * Provider cost and gross profit are NEVER shown to the user — only the
 * per-request user_charge and running balances. Admins see everything in the
 * Admin AI dashboard tab.
 */
const SUGGESTED_PROMPTS = [
  "Summarize the current status of this deal in 3 bullets.",
  "List the missing documents I still need to close.",
  "Explain the fee breakdown for this deal.",
  "How do I check the vehicle title with the DMV in this state?",
];

export default function AiDealAssistantPanel({ dealId, buyerId, sellerId }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const bottomRef = useRef(null);

  const isBuyerSeller = user && (user.user_id === buyerId || user.user_id === sellerId);
  const role = user?.user_id === buyerId ? "buyer" : (user?.user_id === sellerId ? "seller" : null);

  const load = () => {
    api.get(`/ai/deal/${dealId}/status`).then((r) => setStatus(r.data)).catch(() => setStatus(null));
    api.get(`/ai/deal/${dealId}/messages`).then((r) => setMessages(r.data.items || [])).catch(() => {});
  };
  useEffect(() => { if (isBuyerSeller) load(); }, [dealId, isBuyerSeller]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Poll checkout status when returning from Stripe
  useEffect(() => {
    const sid = params.get("ai_session");
    if (!sid || !isBuyerSeller) return;
    let live = true, attempts = 0;
    const poll = async () => {
      try {
        const r = await api.get(`/ai/deal/${dealId}/checkout/status/${sid}`);
        if (r.data.payment_status === "paid") {
          toast.success("AI unlocked — $10 credited to your Deal Assistant wallet");
          const next = new URLSearchParams(params); next.delete("ai_session");
          setParams(next, { replace: true });
          load();
          return;
        }
      } catch {}
      if (live && attempts++ < 8) setTimeout(poll, 2000);
    };
    poll();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, dealId, isBuyerSeller]);

  if (!isBuyerSeller) return null;
  if (!status) return <div className="border border-border p-6 text-sm text-muted-foreground" data-testid="ai-loading">Loading AI…</div>;

  const w = status.wallet || {};
  const locked = !w.initial_deposit_completed;
  const paused = w.status === "paused";
  const lowBalance = w.status === "low_balance" || (w.total_used || 0) >= 8.0;

  const startCheckout = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/ai/deal/${dealId}/checkout`, { origin_url: window.location.origin });
      if (r.data.url) window.location.href = r.data.url;
      else throw new Error("no url");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start checkout");
      setBusy(false);
    }
  };

  const toggleAutoReload = async () => {
    setBusy(true);
    try {
      await api.post(`/ai/deal/${dealId}/auto-reload`, { enabled: !w.auto_reload_enabled });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const pauseResume = async () => {
    setBusy(true);
    try {
      await api.post(`/ai/deal/${dealId}/${paused ? "resume" : "pause"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const send = async (msg) => {
    const content = (msg ?? text).trim();
    if (!content) return;
    setSending(true);
    setText("");
    // Optimistic user message
    const optimistic = { message_id: `tmp_${Date.now()}`, direction: "user", content, at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    try {
      const r = await api.post(`/ai/deal/${dealId}/chat`, { message: content });
      // Reload full history to reconcile with server ids
      const rows = await api.get(`/ai/deal/${dealId}/messages`);
      setMessages(rows.data.items || []);
      setStatus((s) => s ? { ...s, wallet: r.data.wallet } : s);
      if (r.data.low_balance_prompt) {
        toast.warning("You've used $8 of $10 — consider topping up to keep AI running.");
      }
    } catch (e) {
      setMessages((m) => m.filter((x) => x.message_id !== optimistic.message_id));
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  return (
    <div className="border border-border p-5 space-y-4" data-testid="ai-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-overline text-accent">AI Deal Assistant</div>
          <div className="text-xs text-muted-foreground">Private to your side of this deal · Model: {status.config?.model || "gpt"}</div>
        </div>
        <div className="text-right">
          <div className="font-overline text-[10px] text-muted-foreground">Balance</div>
          <div className="font-mono-data text-xl" data-testid="ai-balance">{fmtMoney(w.balance || 0)}</div>
          <div className="text-[10px] font-mono text-muted-foreground">
            Deposited {fmtMoney(w.total_deposited || 0)} · Used {fmtMoney(w.total_used || 0)}
          </div>
        </div>
      </div>

      {locked ? (
        <div className="border border-dashed border-border p-5 text-center space-y-3" data-testid="ai-locked">
          <div className="font-overline text-accent">AI Locked</div>
          <p className="text-sm text-muted-foreground">
            Unlock the AI Deal Assistant for your side of this deal with a one-time <b>${status.config?.initial_deposit?.toFixed(0) || 10} Stripe deposit</b>. The full amount is credited to your AI wallet — usage is billed per request.
          </p>
          <Button className="rounded-none uppercase" onClick={startCheckout} disabled={busy} data-testid="ai-deposit-btn">
            Deposit $10 &amp; Unlock AI
          </Button>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <div className="flex items-center gap-2" data-testid="ai-auto-reload-toggle">
              <Switch checked={!!w.auto_reload_enabled} onCheckedChange={toggleAutoReload} id="ar" disabled={busy} />
              <label htmlFor="ar" className="cursor-pointer">Auto-reload $10 at $8 usage</label>
            </div>
            <Button variant="outline" size="sm" className="rounded-none uppercase text-[10px] h-7" onClick={pauseResume} disabled={busy} data-testid="ai-pause-toggle">
              {paused ? "Resume AI" : "Pause AI"}
            </Button>
            {lowBalance && (
              <Button size="sm" className="rounded-none uppercase text-[10px] h-7" onClick={startCheckout} disabled={busy} data-testid="ai-reload-btn">
                Add $10
              </Button>
            )}
          </div>

          {lowBalance && (
            <div className="border border-amber-400 bg-amber-50 dark:bg-amber-950/20 p-3 text-xs" data-testid="ai-low-balance-banner">
              <b>Low balance.</b> You've used {fmtMoney(w.total_used || 0)} of {fmtMoney(w.total_deposited || 0)}. Remaining: {fmtMoney(w.balance || 0)}. Add $10 to keep the AI running through the rest of this deal.
            </div>
          )}

          <div className="border border-border h-72 overflow-y-auto p-3 space-y-2 bg-muted/20" data-testid="ai-messages">
            {messages.length === 0 && (
              <div className="text-center text-muted-foreground text-sm py-8" data-testid="ai-messages-empty">
                Start by asking a question or picking a suggested prompt below.
              </div>
            )}
            {messages.map((m) => (
              <div key={m.message_id}
                    className={`text-sm px-3 py-2 border-l-2 ${m.direction === "user" ? "border-primary bg-primary/5" : "border-accent bg-background"}`}
                    data-testid={`ai-msg-${m.direction}`}>
                <div className="font-overline text-[9px] text-muted-foreground mb-1">
                  {m.direction === "user" ? "You" : "Assistant"} · {m.at ? new Date(m.at).toLocaleTimeString() : ""}
                </div>
                <div className="whitespace-pre-wrap">{m.content}</div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {!paused && (
            <>
              <div className="flex flex-wrap gap-1 text-[10px]">
                {SUGGESTED_PROMPTS.map((p, i) => (
                  <Button key={i} size="sm" variant="outline" className="rounded-none text-[10px] h-7 uppercase"
                          onClick={() => send(p)} disabled={sending} data-testid={`ai-prompt-${i}`}>
                    {p.length > 40 ? p.slice(0, 40) + "…" : p}
                  </Button>
                ))}
              </div>

              <div className="flex gap-2 items-end">
                <Textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Ask the AI Deal Assistant… (Shift+Enter for newline)"
                  rows={2}
                  className="rounded-none flex-1 font-mono text-sm"
                  disabled={sending}
                  data-testid="ai-chat-input"
                />
                <Button className="rounded-none uppercase h-full" onClick={() => send()} disabled={sending || !text.trim()} data-testid="ai-send-btn">
                  {sending ? "…" : "Send"}
                </Button>
              </div>

              <p className="text-[10px] text-muted-foreground">
                Disclaimer: AI responses may contain errors. Verify all material information independently before closing. AI cannot approve deals, release funds, or provide legal/tax/investment advice.
              </p>
            </>
          )}
        </>
      )}
    </div>
  );
}
